import type { TFunction } from "i18next";
import { isAutoConvertedIsoDateString } from "../../utils/dateUtils";
import type { PrintoutSettings } from "./Header/usePrintout";

/**
 * Saved Detailed-printout presets ("Printout 2", "Printout 3", …).
 *
 * Printout 1 is the user's existing printout settings (the `user_settings` columns) and is never sent
 * through this module. Presets 2+ are stored server-side as a JSON list and only drive the Detailed
 * printout; Summary, Spreadsheet and 530 always print with Printout 1's settings.
 */

/** Mirrors `MAX_PRINTOUT_PRESETS` in `backend/models/printout_presets.py`. Counts Printout 1 too. */
export const MAX_PRINTOUT_PRESETS = 3;

/** Mirrors `PRINTOUT_PRESET_NAME_MAX_LENGTH` in `backend/models/printout_presets.py`. */
export const PRINTOUT_PRESET_NAME_MAX_LENGTH = 40;

/** The first stored preset is "Printout 2" — "Printout 1" is the fixed label of the user's main settings. */
const FIRST_STORED_PRESET_NUMBER = 2;

/** Settings that only exist in the Summary printout, so a Detailed-only preset never carries them. */
type SummaryOnlySettingKey = "showDhppd" | "showNhppd" | "showActualStaffColumn" | "showActualHoursColumn" | "hppdIncludeTrainees";

/**
 * One preset's settings exactly as they cross the wire: every Detailed setting, with the column
 * widths as an explicit `null` when the user never dragged one (the backend model rejects a missing key).
 *
 * `customSheetTabs` and `showAdmins` are non-null here and in the backend's stored-preset model,
 * which rejects null for them; only `columnGroupingBy` and the column widths may be null.
 */
export type PresetSettings = Omit<PrintoutSettings, SummaryOnlySettingKey | "printoutColumnWidths"> & {
	printoutColumnWidths: Record<string, number> | null;
};

/**
 * Every preset setting key, as an object so the compiler checks it both ways: a key added to or
 * removed from `PrintoutSettings` (outside the Summary-only set) breaks this literal until it is
 * listed here — and `printoutPresets.spec.ts` then fails until the backend's
 * `shared/contracts/printout_preset_fields.json` lists it too.
 */
export const PRESET_SETTING_KEYS: Record<keyof PresetSettings, true> = {
	printGaps: true,
	showRoleGroupStats: true,
	dailyPrintoutGrouping: true,
	divideGroupsByPage: true,
	separateSheetsBy: true,
	showPtoSheet: true,
	showCallOutsSheet: true,
	includeTimeOffReason: true,
	columns: true,
	sortBy: true,
	staffPerRow: true,
	rowGroupingBy: true,
	columnGroupingBy: true,
	spaceBetweenGroups: true,
	orientation: true,
	fitToPage: true,
	fitToWidth: true,
	fitToHeight: true,
	hideHppdNursingHours: true,
	flagNotOnboarded: true,
	flagAgency: true,
	mergeSimilarShiftTimes: true,
	alignGroupTimesToPopular: true,
	customTimeGroups: true,
	appendRoleToName: true,
	hideColumnNames: true,
	appendEmptyColumns: true,
	customColumnNames: true,
	customSheetTabs: true,
	showAdmins: true,
	showLocationCensus: true,
	printoutColumnWidths: true,
};

/** A stored preset (Printout 2+). */
export type PrintoutPreset = {
	id: string;
	name: string;
	settings: PresetSettings;
};

/** Every printout-presets endpoint answers with the user's full preset state for the department. */
export type PrintoutPresetsResponse = {
	/** `null` means Printout 1 (the user's main printout settings) is the one in use. */
	activePresetId: string | null;
	/** Stored presets only — Printout 1 is never listed. */
	presets: PrintoutPreset[];
};

/** The Detailed settings of `printoutSettings`, as the exact 32-key body the presets API accepts. */
export const toPresetSettings = (printoutSettings: PrintoutSettings): PresetSettings => ({
	printGaps: printoutSettings.printGaps,
	showRoleGroupStats: printoutSettings.showRoleGroupStats,
	dailyPrintoutGrouping: printoutSettings.dailyPrintoutGrouping,
	divideGroupsByPage: printoutSettings.divideGroupsByPage,
	separateSheetsBy: printoutSettings.separateSheetsBy,
	showPtoSheet: printoutSettings.showPtoSheet,
	showCallOutsSheet: printoutSettings.showCallOutsSheet,
	includeTimeOffReason: printoutSettings.includeTimeOffReason,
	columns: printoutSettings.columns,
	sortBy: printoutSettings.sortBy,
	staffPerRow: printoutSettings.staffPerRow,
	rowGroupingBy: printoutSettings.rowGroupingBy,
	columnGroupingBy: printoutSettings.columnGroupingBy,
	spaceBetweenGroups: printoutSettings.spaceBetweenGroups,
	orientation: printoutSettings.orientation,
	fitToPage: printoutSettings.fitToPage,
	fitToWidth: printoutSettings.fitToWidth,
	fitToHeight: printoutSettings.fitToHeight,
	hideHppdNursingHours: printoutSettings.hideHppdNursingHours,
	flagNotOnboarded: printoutSettings.flagNotOnboarded,
	flagAgency: printoutSettings.flagAgency,
	mergeSimilarShiftTimes: printoutSettings.mergeSimilarShiftTimes,
	alignGroupTimesToPopular: printoutSettings.alignGroupTimesToPopular,
	customTimeGroups: printoutSettings.customTimeGroups,
	appendRoleToName: printoutSettings.appendRoleToName,
	hideColumnNames: printoutSettings.hideColumnNames,
	appendEmptyColumns: printoutSettings.appendEmptyColumns,
	customColumnNames: printoutSettings.customColumnNames,
	customSheetTabs: printoutSettings.customSheetTabs,
	showAdmins: printoutSettings.showAdmins,
	showLocationCensus: printoutSettings.showLocationCensus,
	// DEFAULT: undefined means "no column was ever dragged"; the wire spells that as an explicit null.
	printoutColumnWidths: printoutSettings.printoutColumnWidths ?? null,
});

/**
 * The panel settings for a preset: the preset's Detailed settings over `base` (Printout 1's settings),
 * which still supplies the Summary-only toggles a preset doesn't carry.
 */
export const applyPresetSettings = (base: PrintoutSettings, preset: PresetSettings): PrintoutSettings => {
	const { printoutColumnWidths, ...detailedSettings } = preset;
	return {
		...base,
		...detailedSettings,
		// DEFAULT: null on the wire is "no column was ever dragged", which `PrintoutSettings` spells undefined.
		printoutColumnWidths: printoutColumnWidths ?? undefined,
	};
};

/** Whether `storedPresetCount` presets plus Printout 1 already fill every slot. */
export const isAtPresetLimit = (storedPresetCount: number): boolean => storedPresetCount + 1 >= MAX_PRINTOUT_PRESETS;

/**
 * Plain `toLowerCase`, not `toLocaleLowerCase`: the server compares with Python's `lower()`, which ignores
 * the browser's locale — a Turkish-locale browser would otherwise disagree with it about "I".
 */
const normalizeName = (name: string): string => name.trim().toLowerCase();

/**
 * Whether `name` collides, case-insensitively and ignoring surrounding spaces, with another printout.
 * For a rename, leave the preset's own current name out of `existingNames`.
 */
export const isPresetNameTaken = (name: string, existingNames: readonly string[], printout1Label: string): boolean => {
	const normalized = normalizeName(name);
	return normalizeName(printout1Label) === normalized || existingNames.some((existing) => normalizeName(existing) === normalized);
};

/** The localized "Printout N" for the smallest N ≥ 2 that no existing printout is already called. */
export const nextPresetName = (existingNames: readonly string[], t: TFunction): string => {
	// N existing names can block at most N candidates, so this always returns within N + 1 tries.
	for (let number = FIRST_STORED_PRESET_NUMBER; ; number += 1) {
		const candidate = t("printout.preset.name.numbered", { number });
		if (!existingNames.some((existing) => normalizeName(existing) === normalizeName(candidate))) return candidate;
	}
};

export type PresetNameError = "empty" | "tooLong" | "taken" | "dateOnly";

/** i18n key for each name problem, so every surface that edits a name says the same thing. */
export const PRESET_NAME_ERROR_KEYS: Record<PresetNameError, string> = {
	empty: "printout.preset.name.error.empty",
	tooLong: "printout.preset.name.error.too.long",
	taken: "printout.preset.name.error.taken",
	dateOnly: "printout.preset.name.error.date.only",
};

/**
 * What's wrong with a typed preset name, or `null` when it can be saved. Length counts characters
 * after trimming, so send the trimmed name. For a rename, leave the preset's own name out of
 * `existingNames`.
 */
export const validatePresetName = (name: string, existingNames: readonly string[], printout1Label: string): PresetNameError | null => {
	const trimmed = name.trim();
	if (trimmed.length === 0) return "empty";
	// Spread so an emoji counts once, matching the server's code-point length.
	if ([...trimmed].length > PRINTOUT_PRESET_NAME_MAX_LENGTH) return "tooLong";
	// Every response is passed through `convertIsoDates`, so a name that is only a date would come back
	// from the server as a Luxon DateTime rather than the text the user typed.
	if (isAutoConvertedIsoDateString(trimmed)) return "dateOnly";
	if (isPresetNameTaken(trimmed, existingNames, printout1Label)) return "taken";
	return null;
};
